<script lang="ts">
	// The design system's TablePanel (design/components/surfaces/TablePanel.jsx):
	// a panel of rows under one header.
	//
	// The columns are declared once, here. The panel publishes their tracks as
	// --cols, and every Row reads them back, so a row cannot disagree with its
	// own header. Rows never set widths of their own. Below rowMin the panel
	// slides sideways rather than crushing its columns.

	import type { Snippet } from 'svelte';

	interface Column {
		label: string;
		/** A grid track. Defaults to an equal share. */
		w?: string;
		/** 'end' for a figure column, so its label sits over the digits. */
		align?: 'end';
	}

	interface Props {
		columns: Column[];
		title?: string;
		note?: string;
		rowMin?: string;
		testId?: string;
		children: Snippet;
	}

	let { columns, title, note, rowMin = 'var(--row-min)', testId, children }: Props = $props();

	const template = $derived(columns.map((column) => column.w ?? 'minmax(0,1fr)').join(' '));
</script>

<div class="panel" style:--cols={template} style:--panel-row-min={rowMin} data-testid={testId}>
	{#if title}
		<div class="head">
			<p class="title">{title}</p>
			{#if note}
				<p class="note">{note}</p>
			{/if}
		</div>
	{/if}
	<div class="columns">
		{#each columns as column, index (index)}
			<!-- A figure column's header sits over the digits it names. -->
			<span class:end={column.align === 'end'}>{column.label}</span>
		{/each}
	</div>
	<div class="rows">{@render children()}</div>
</div>

<style>
	.panel {
		background: var(--surface-card);
		border: 1px solid var(--border-default);
		border-radius: var(--radius-panel);
		overflow: hidden;
		overflow-x: auto;
	}

	.head {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		align-items: baseline;
		justify-content: space-between;
		min-inline-size: var(--panel-row-min);
		padding: var(--panel-header-pad);
		background: var(--surface-card-header);
		border-block-end: 1px solid var(--border-default);
	}

	.title {
		margin: 0;
		font-size: 16px;
		font-weight: 600;
	}

	.note {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.columns {
		display: grid;
		grid-template-columns: var(--cols);
		gap: var(--col-gap);
		min-inline-size: var(--panel-row-min);
		padding: 12px 20px;
		background: var(--surface-card-header);
		border-block-end: 1px solid var(--border-default);
		/* The rows reserve a 2px leading edge for their states; the header keeps
		   the same, so a column's label sits over its values. */
		border-inline-start: 2px solid transparent;
		font-family: var(--font-mono);
		font-size: 11.5px;
		letter-spacing: var(--tracking-label);
		color: var(--text-muted);
	}

	.columns span {
		min-inline-size: 0;
	}

	.columns .end {
		text-align: end;
	}

	.rows {
		min-inline-size: var(--panel-row-min);
	}
</style>
